import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Pencil, Trash2, Plus, Save, X } from 'lucide-react';
import DataTable from '../components/DataTable';
import SearchBar from '../components/SearchBar';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import ConfirmDialog from '../components/ConfirmDialog';
import DateRangeFilter from '../components/DateRangeFilter';
import DateInput from '../components/DateInput';
import { useLanguage } from '../context/LanguageContext';

const EMPTY_FORM = { badge_num: '', id_emp_la: '', date_la: '', time_la: '', pto_hours: '', reason_la: '' };

export default function LateAuth() {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [empName, setEmpName] = useState('');
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const limit = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/late-auth', { params: { employee: search, dateFrom, dateTo, page, limit } });
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('la.fetchError') || 'Error loading authorizations.' });
    }
    setLoading(false);
  }, [search, dateFrom, dateTo, page, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const lookupEmployee = async (badge) => {
    if (!badge) { setEmpName(''); setForm(f => ({ ...f, id_emp_la: '' })); return; }
    try {
      const res = await axios.get(`/api/employees/badge/${badge}`);
      setEmpName(`${res.data.name_emp} ${res.data.last_name_emp}`);
      setForm(f => ({ ...f, id_emp_la: res.data.id_emp }));
    } catch {
      setEmpName(t('abs.notFound'));
      setForm(f => ({ ...f, id_emp_la: '' }));
    }
  };

  const searchEmployees = async (query) => {
    setEmpSearch(query);
    if (!query || query.length < 2) { setEmpResults([]); return; }
    try {
      const res = await axios.get('/api/employees', { params: { search: query } });
      const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setEmpResults(list.slice(0, 8));
    } catch { setEmpResults([]); }
  };

  const selectEmployee = (emp) => {
    setForm(f => ({ ...f, badge_num: String(emp.badge_num_emp), id_emp_la: emp.id_emp }));
    setEmpName(`${emp.name_emp} ${emp.last_name_emp}`);
    setEmpSearch(`${emp.name_emp} ${emp.last_name_emp}`);
    setEmpResults([]);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setEmpName('');
    setEmpSearch('');
    setEmpResults([]);
    setEditReason('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      badge_num: row.badge_num_emp || '',
      id_emp_la: row.id_emp_la,
      date_la: row.date_la ? row.date_la.substring(0, 10) : '',
      time_la: row.time_la ? String(row.time_la).substring(0, 5) : '',
      pto_hours: row.pto_hours != null && Number(row.pto_hours) > 0 ? Number(row.pto_hours) : '',
      reason_la: row.reason_la || '',
    });
    setEmpName(row.name_la ? `${row.name_la} ${row.last_name_la}` : '');
    setEmpSearch('');
    setEmpResults([]);
    setEditReason('');
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.id_emp_la) { Swal.fire({ icon: 'warning', title: t('abs.invalidEmp'), text: t('abs.invalidEmpMsg') }); return; }
    if (!form.date_la) { Swal.fire({ icon: 'warning', title: t('common.error'), text: t('la.dateReq') || 'Date is required.' }); return; }
    if (editing && !editReason.trim()) {
      Swal.fire({ icon: 'warning', title: t('audit.reasonRequired') || 'Razon requerida', text: t('audit.reasonRequiredMsg') || 'Indica el motivo del cambio.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id_emp_la: form.id_emp_la,
        date_la: form.date_la,
        time_la: form.time_la || null,
        pto_hours: form.pto_hours === '' ? 0 : Number(form.pto_hours),
        reason_la: form.reason_la,
        reason: editing ? editReason : undefined,
      };
      if (editing) {
        await axios.put(`/api/late-auth/${editing.id_la}`, payload);
      } else {
        await axios.post('/api/late-auth', payload);
      }
      setModalOpen(false);
      await fetchData();
      Swal.fire({ icon: 'success', title: editing ? t('la.updated') : t('la.created'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.saveError') });
    }
    setSaving(false);
  };

  const askDelete = async (id) => {
    const { value: reasonInput } = await Swal.fire({
      title: t('la.deleteConfirm') || 'Eliminar autorizacion?',
      input: 'textarea',
      inputLabel: t('audit.reasonRequired') || 'Razon (obligatoria)',
      inputPlaceholder: t('audit.reasonPh') || 'Por que se esta eliminando?',
      inputValidator: (v) => !v?.trim() && (t('audit.reasonRequiredMsg') || 'La razon es obligatoria.'),
      showCancelButton: true,
      confirmButtonText: t('common.delete') || 'Eliminar',
      cancelButtonText: t('common.cancel') || 'Cancelar',
      confirmButtonColor: '#ef4444',
    });
    if (!reasonInput) return;
    setDeleteReason(reasonInput);
    setDeleteId(id);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/late-auth/${deleteId}`, { data: { reason: deleteReason } });
      setDeleteId(null);
      setDeleteReason('');
      fetchData();
      Swal.fire({ icon: 'success', title: t('la.deleted'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.deleteError') });
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const s = typeof d === 'string' ? d.substring(0, 10) : d;
    const dt = new Date(s + (typeof s === 'string' && s.length === 10 ? 'T00:00:00' : ''));
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US');
  };

  const fmtShift = (s) => (s ? String(s).substring(0, 5) : '-');

  const fmtTime12h = (s) => {
    if (!s) return '-';
    const [hh, mm] = String(s).substring(0, 5).split(':');
    let h = Number(hh);
    const ampm = h >= 12 ? 'PM' : 'AM';
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return `${h}:${mm} ${ampm}`;
  };

  const columns = [
    { header: t('common.badge'), accessor: 'badge_num_emp' },
    { header: t('abs.employee2'), render: (r) => r.name_la ? `${r.name_la} ${r.last_name_la}` : '-' },
    { header: t('la.dateCol') || 'Date', render: (r) => formatDate(r.date_la) },
    {
      header: t('la.timeCol') || 'Authorized time',
      render: (r) => r.time_la
        ? <span className="badge badge-success" style={{ fontSize: 12 }}>{fmtTime12h(r.time_la)}</span>
        : <span style={{ color: 'var(--text-muted)' }}>-</span>,
    },
    {
      header: t('la.ptoCol') || 'PTO',
      render: (r) => Number(r.pto_hours) > 0
        ? <span className="badge badge-info" style={{ fontSize: 12 }}>{Number(r.pto_hours)} h</span>
        : <span style={{ color: 'var(--text-muted)' }}>-</span>,
    },
    { header: t('la.shiftCol') || 'Shift start', render: (r) => fmtShift(r.shift_start) },
    { header: t('la.note') || 'Note', render: (r) => r.reason_la || '-' },
    {
      header: t('common.actions'),
      style: { width: 120 },
      render: (r) => (
        <div className="table-actions">
          <button className="btn-secondary btn-sm" onClick={() => openEdit(r)}><Pencil size={14} style={{ marginRight: 4 }} />{t('common.edit')}</button>
          <button className="btn-danger btn-sm" onClick={() => askDelete(r.id_la)}><Trash2 size={14} style={{ marginRight: 4 }} />{t('common.delete')}</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('la.title') || 'Authorized late arrivals'}</h1>
        <div className="page-header-actions">
          <button className="btn-primary" style={{ width: 'auto', padding: '8px 20px', whiteSpace: 'nowrap' }} onClick={openCreate}><Plus size={16} style={{ marginRight: 4, flexShrink: 0 }} />{t('common.newFem')}</button>
        </div>
      </div>

      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: -8, marginBottom: 16 }}>
        {t('la.help') || 'When an employee is authorized to arrive late on a date, the late alert (yellow/orange) is suppressed for that day. The hour calculation does not change — the real punch time is still used.'}
      </p>

      <div className="filters-bar">
        <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={t('abs.search')} />
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onDateFromChange={(v) => { setDateFrom(v); setPage(1); }} onDateToChange={(v) => { setDateTo(v); setPage(1); }} onRefresh={() => { setDateFrom(''); setDateTo(''); setPage(1); }} />
      </div>

      <div className="table-container">
        <DataTable columns={columns} data={data} loading={loading} emptyMessage={t('la.empty') || 'No authorizations found.'} />
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? (t('la.editTitle') || 'Edit authorization') : (t('la.newTitle') || 'New authorization')}>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            {/* Employee search section */}
            <div style={{
              padding: 16, marginBottom: 16,
              background: 'var(--bg-tertiary)', borderRadius: 'var(--radius)',
              border: empName && empName !== t('abs.notFound') ? '2px solid var(--success)' : '1px solid var(--border)',
            }}>
              <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>{t('abs.employee')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  placeholder={t('abs.employeeSearch')}
                  value={empSearch}
                  onChange={e => {
                    searchEmployees(e.target.value);
                    if (/^\d+$/.test(e.target.value)) {
                      setForm(f => ({ ...f, badge_num: e.target.value }));
                    }
                  }}
                  onBlur={() => {
                    if (/^\d+$/.test(empSearch) && !form.id_emp_la) lookupEmployee(empSearch);
                    setTimeout(() => setEmpResults([]), 200);
                  }}
                  style={{ fontSize: 15 }}
                />
                {empResults.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: 'var(--bg-primary)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', maxHeight: 220, overflowY: 'auto',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  }}>
                    {empResults.map(emp => (
                      <div key={emp.id_emp}
                        onMouseDown={() => selectEmployee(emp)}
                        style={{
                          padding: '10px 14px', cursor: 'pointer',
                          borderBottom: '1px solid var(--border)',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ fontWeight: 600 }}>{emp.name_emp} {emp.last_name_emp}</span>
                        <span className="badge badge-info" style={{ fontSize: 11 }}>Badge {emp.badge_num_emp}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {empName && empName !== t('abs.notFound') && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="badge badge-success" style={{ fontSize: 12, padding: '4px 10px' }}>Badge {form.badge_num}</span>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{empName}</span>
                </div>
              )}
              {empName === t('abs.notFound') && (
                <small style={{ color: 'var(--danger)', marginTop: 6, display: 'block' }}>{t('abs.notFound')}</small>
              )}
            </div>

            {/* Date + Time + PTO */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('la.dateCol') || 'Date'} <span style={{ color: 'var(--danger)' }}>*</span></label>
                <DateInput className="form-input" required value={form.date_la}
                  onChange={v => setForm({ ...form, date_la: v })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('la.timeCol') || 'Authorized time'}</label>
                <input type="time" className="form-input" value={form.time_la}
                  onChange={e => setForm({ ...form, time_la: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('la.ptoCol') || 'PTO (hrs)'}</label>
                <input type="number" min="0" step="0.5" className="form-input" value={form.pto_hours}
                  onChange={e => setForm({ ...form, pto_hours: e.target.value })}
                  placeholder={t('la.ptoPh') || 'e.g. 8'} />
              </div>
            </div>

            {/* Note (full width) */}
            <div className="form-row">
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label">{t('la.note') || 'Note'}</label>
                <input className="form-input" value={form.reason_la}
                  onChange={e => setForm({ ...form, reason_la: e.target.value })}
                  placeholder={t('la.notePh') || 'e.g. notified by chat'} />
              </div>
            </div>

            {editing && (
              <div className="form-row">
                <div className="form-group" style={{ width: '100%' }}>
                  <label className="form-label">
                    {t('audit.reason') || 'Razon'} <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <textarea
                    className="form-input"
                    rows={2}
                    value={editReason}
                    onChange={e => setEditReason(e.target.value)}
                    placeholder={t('audit.reasonPh') || 'Por que se esta editando?'}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }} disabled={saving || !form.id_emp_la}>
              <Save size={15} style={{ marginRight: 4 }} />{saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        message={t('la.deleteConfirm') || 'Eliminar autorizacion?'}
      />
    </div>
  );
}
